import { usePlanningStore } from '@/stores/planning';
import { CreateDropdown } from './CreateDropdown';
import { WeekNav } from './WeekNav';
import { FilterDropdown } from './FilterDropdown';
import { ProjectSearchInput } from './ProjectSearchInput';
import { ZoomControl } from './PlanningZoom';

interface PlanningHeaderProps {
  filteredCount?: number;
}

function RefreshButton() {
  const loading = usePlanningStore((s) => s.loading);
  const fetchPlanning = usePlanningStore((s) => s.fetchPlanning);

  return (
    <button
      type="button"
      onClick={() => fetchPlanning()}
      disabled={loading}
      className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
      aria-label="Vernieuwen"
    >
      <svg
        className={`w-4 h-4 text-td-navy ${loading ? 'animate-spin' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    </button>
  );
}

export function PlanningHeader({ filteredCount }: PlanningHeaderProps) {
  const tasks = usePlanningStore((s) => s.tasks);
  const lastRefreshed = usePlanningStore((s) => s.lastRefreshed);
  // "Per dag" is an overview: nothing to create into (no installer cells).
  const overviewOnly = usePlanningStore((s) => s.viewFilter === 'per_day');

  const refreshTime = lastRefreshed
    ? lastRefreshed.toLocaleTimeString('nl-BE', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="px-4 lg:px-8 pt-4 lg:pt-6 pb-3">
      {/* Desktop: three equal columns — week nav always centered */}
      <div className="hidden lg:grid grid-cols-3 items-center gap-4">
        <div className="flex items-center gap-2 justify-self-start">
          {!overviewOnly && <CreateDropdown />}
          <ProjectSearchInput />
        </div>
        <div className="justify-self-center">
          <WeekNav />
        </div>
        <div className="flex items-center gap-2 justify-self-end">
          <ZoomControl />
          <FilterDropdown />
          <RefreshButton />
        </div>
      </div>

      {/* Mobile: search on top, actions below */}
      <div className="lg:hidden">
        <ProjectSearchInput />
        <div className="flex items-center gap-2 mt-2">
          {!overviewOnly && <CreateDropdown />}
          <div className="flex-1 flex justify-center">
            <WeekNav />
          </div>
          <FilterDropdown />
          <RefreshButton />
        </div>
      </div>

      {/* Task count + refresh time */}
      <p className="text-xs text-td-muted mt-2">
        {filteredCount != null && filteredCount !== tasks.length
          ? `${filteredCount} van ${tasks.length} taken`
          : `${tasks.length} taken gevonden`}
        {refreshTime && <span> · vernieuwd om {refreshTime}</span>}
      </p>
    </div>
  );
}
